<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';

type Props = {
  moreStr: string;
  lessStr: string;
  text: string;
  link: string;
  maxChars: number;
  htmlFormat: boolean;
  customMoreAriaLabel?: string;
  customLessAriaLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
  moreStr: '',
  lessStr: '',
  text: '',
  link: '#',
  maxChars: 100,
  htmlFormat: false,
});
const { moreStr, lessStr, text, maxChars, link } = toRefs(props);
const isReadMore = ref(false);
const formattedString = computed(() => {
  let valContainer = text.value.replace(/style="[^"]*color:[^;"]*;?[^"]*"/gi, '');

  if (!isReadMore.value && text.value.length > maxChars.value) {
    valContainer = `${valContainer.substring(0, maxChars.value)}...`;
  }

  return valContainer;
});

const triggerReadMore = (e: { preventDefault: () => void }, b: boolean) => {
  if (link?.value === '#') {
    e.preventDefault();
  }
  if (lessStr.value !== null || lessStr.value !== '') isReadMore.value = b;
};

const moreAriaLabel = computed(() => props.customMoreAriaLabel || moreStr.value);
const lessAriaLabel = computed(() => props.customLessAriaLabel || lessStr.value);
</script>

<template>
  <div>
    <div v-if="htmlFormat" class="rich-text" v-html="formattedString" />
    <span v-else v-text="formattedString" />
    <span v-show="text.length > maxChars">
      <a
        v-show="!isReadMore && moreStr"
        :href="link"
        class="read-more pa-3"
        @click="triggerReadMore($event, true)"
        :aria-label="moreAriaLabel"
      >
        {{ moreStr }}
      </a>
      <a
        v-show="isReadMore && lessStr"
        :href="link"
        class="read-more pl-2"
        @click="triggerReadMore($event, false)"
        :aria-label="lessAriaLabel"
      >
        {{ lessStr }}
      </a>
    </span>
    <slot></slot>
  </div>
</template>

<style scoped lang="scss">
.rich-text {
  :deep(p) {
    margin-bottom: 0.75em;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
